import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Save, Check, FileDown, FilePlus2, Pencil, Trash2 } from "lucide-react";
import { MaskLines, Reveal } from "../components/anim";
import { Eyebrow } from "../components/ui-bits";
import useSEO from "../hooks/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "sprz-admin-token";
const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", useGrouping: "always" }).format(Number(n));

const EMPTY_REPORT = { nome_evento: "", organizzazione: "", data_evento: "", localita: "", drink_venduti: 0, quota_drink: 1.5, note: "", stato: "Bozza" };

export default function Admin() {
  useSEO("Pannello amministrativo | Sprizziamoci");
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [cfg, setCfg] = useState(null);
  const [stats, setStats] = useState(null);
  const [iscritti, setIscritti] = useState(null);
  const [reports, setReports] = useState(null);
  const [rShow, setRShow] = useState(false);
  const [rEdit, setREdit] = useState(null);
  const [rForm, setRForm] = useState(EMPTY_REPORT);
  const [rBusy, setRBusy] = useState(false);
  const [login, setLogin] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/admin/config`, auth)
      .then((r) => setCfg(r.data))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        toast.error("Sessione scaduta, accedi di nuovo");
      });
    axios
      .get(`${API}/admin/stats`, auth)
      .then((r) => setStats(r.data.pagine || []))
      .catch(() => setStats([]));
    axios
      .get(`${API}/admin/partner-requests`, auth)
      .then((r) => setIscritti(r.data.iscritti || []))
      .catch(() => setIscritti([]));
    axios
      .get(`${API}/admin/reports`, auth)
      .then((r) => setReports(r.data.reports || []))
      .catch(() => setReports([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const cambiaStato = async (id, stato) => {
    try {
      await axios.put(`${API}/admin/partner-requests/${id}`, { stato }, auth);
      setIscritti((v) => v.map((x) => (x._id === id ? { ...x, stato } : x)));
      toast.success("Stato della trattativa aggiornato");
    } catch {
      toast.error("Aggiornamento non riuscito");
    }
  };

  const salvaReport = async (e) => {
    e.preventDefault();
    setRBusy(true);
    const quotaTot = Math.round(rForm.drink_venduti * rForm.quota_drink * 100) / 100;
    try {
      if (rEdit) {
        await axios.put(`${API}/admin/reports/${rEdit}`, rForm, auth);
        setReports((v) => v.map((x) => (x._id === rEdit ? { ...x, ...rForm, quota_totale: quotaTot } : x)));
        toast.success("Report aggiornato");
      } else {
        const { data } = await axios.post(`${API}/admin/reports`, rForm, auth);
        setReports((v) => [{ _id: data.id, ...rForm, quota_totale: quotaTot }, ...v]);
        toast.success("Report creato");
      }
      setRShow(false);
      setREdit(null);
      setRForm(EMPTY_REPORT);
    } catch {
      toast.error("Salvataggio del report non riuscito");
    } finally {
      setRBusy(false);
    }
  };

  const eliminaReport = async (id) => {
    try {
      await axios.delete(`${API}/admin/reports/${id}`, auth);
      setReports((v) => v.filter((x) => x._id !== id));
      toast.success("Report eliminato");
    } catch {
      toast.error("Eliminazione non riuscita");
    }
  };

  const cambiaStatoReport = async (id, stato) => {
    const r = reports.find((x) => x._id === id);
    if (!r) return;
    const { _id, quota_totale, created_at, updated_at, ...payload } = r;
    try {
      await axios.put(`${API}/admin/reports/${id}`, { ...payload, stato }, auth);
      setReports((v) => v.map((x) => (x._id === id ? { ...x, stato } : x)));
      toast.success("Stato del report aggiornato");
    } catch {
      toast.error("Aggiornamento non riuscito");
    }
  };

  const scaricaRendiconto = async (r) => {
    try {
      const res = await axios.get(`${API}/admin/reports/${r._id}/pdf`, { ...auth, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rendiconto-${(r.nome_evento || "evento").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 30)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download del rendiconto non riuscito");
    }
  };

  const apriModificaReport = (r) => {
    setREdit(r._id);
    setRShow(true);
    setRForm({
      nome_evento: r.nome_evento,
      organizzazione: r.organizzazione,
      data_evento: r.data_evento || "",
      localita: r.localita || "",
      drink_venduti: r.drink_venduti,
      quota_drink: r.quota_drink,
      note: r.note || "",
      stato: r.stato || "Bozza",
    });
  };

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, login);
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success("Bentornato!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Credenziali non valide");
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/admin/config`, cfg, auth);
      setCfg(data);
      toast.success("Configurazione salvata e sede geocodificata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Salvataggio non riuscito");
    } finally {
      setBusy(false);
    }
  };

  if (!token)
    return (
      <div data-testid="admin-login" className="pt-40 pb-32">
        <div className="mx-auto max-w-md px-5">
          <Eyebrow>Area riservata</Eyebrow>
          <h1 className="mt-4 font-display text-4xl md:text-5xl font-semibold">Pannello amministrativo</h1>
          <form onSubmit={doLogin} className="mt-10 space-y-5">
            <input
              data-testid="admin-field-email"
              type="email"
              className="field"
              placeholder="Email amministratore"
              value={login.email}
              onChange={(e) => setLogin((l) => ({ ...l, email: e.target.value }))}
            />
            <input
              data-testid="admin-field-password"
              type="password"
              className="field"
              placeholder="Password"
              value={login.password}
              onChange={(e) => setLogin((l) => ({ ...l, password: e.target.value }))}
            />
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-brand-ink hover:bg-[#ff9433] transition-colors duration-300 disabled:opacity-60"
            >
              {busy ? <Loader2 size={15} className="animate-spin" /> : "Accedi"}
            </button>
          </form>
        </div>
      </div>
    );

  if (!cfg)
    return <div className="pt-40 pb-40 text-center text-white/50 text-sm">Caricamento…</div>;

  const setField = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setPrezzo = (id, v) =>
    setCfg((c) => ({
      ...c,
      listino: c.listino.map((it) => (it.id === id ? { ...it, prezzo: Number(v) || 0 } : it)),
    }));

  return (
    <div data-testid="admin-panel" className="pt-32 md:pt-44 pb-24">
      <div className="mx-auto max-w-3xl px-5 md:px-8">
        <div className="flex items-start justify-between gap-6">
          <div>
            <Eyebrow>Area riservata</Eyebrow>
            <MaskLines
              lines={["CONFIGURAZIONE", "PREVENTIVI."]}
              when="load"
              lineClass="font-display font-semibold tracking-tight leading-[1] text-[clamp(2rem,5vw,3.6rem)]"
              className="mt-4"
            />
          </div>
          <button
            data-testid="admin-logout"
            onClick={() => {
              localStorage.removeItem(TOKEN_KEY);
              setToken(null);
            }}
            className="mt-2 inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-[11px] uppercase tracking-[0.15em] text-white/60 hover:border-brand-orange hover:text-brand-orange transition-colors duration-200"
          >
            <LogOut size={13} /> Esci
          </button>
        </div>

        <Reveal className="mt-12">
          <div className="rounded-3xl border border-white/[0.08] bg-brand-surface/40 p-8 md:p-10 space-y-6">
            <p className="eyebrow">Trasferta e IVA</p>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-white/50 mb-2.5">
                Indirizzo di partenza (sede)
              </label>
              <input
                data-testid="admin-field-sede"
                className="field"
                value={cfg.sede}
                onChange={(e) => setField("sede", e.target.value)}
              />
              {cfg.sede_coords && (
                <p data-testid="admin-geocode-ok" className="mt-2 flex items-center gap-1.5 text-[11px] text-white/40">
                  <Check size={12} className="text-brand-orange" /> Geocodificata: {cfg.sede_coords.display_name}
                </p>
              )}
            </div>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
              {[
                { id: "tariffa_km", label: "Tariffa €/km A/R", step: "0.05" },
                { id: "trasferta_minima", label: "Trasferta minima €", step: "1" },
                { id: "iva", label: "IVA %", step: "0.5" },
                { id: "noleggio_fisso", label: "Noleggio banchi €", step: "5" },
                { id: "montaggio_fisso", label: "Montaggio e smontaggio €", step: "5" },
                { id: "prezzo_fusto", label: "Prezzo per fusto €", step: "10" },
                { id: "porzioni_fusto", label: "Bicchieri per fusto (20 L)", step: "1" },
                { id: "prezzo_banco_extra", label: "Banco aggiuntivo €", step: "10" },
              ].map((f) => (
                <div key={f.id}>
                  <label className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-white/50 mb-2.5">
                    {f.label}
                  </label>
                  <input
                    data-testid={`admin-field-${f.id}`}
                    type="number"
                    step={f.step}
                    className="field"
                    value={cfg[f.id]}
                    onChange={(e) => setField(f.id, e.target.value)}
                  />
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal className="mt-8">
          <div className="rounded-3xl border border-white/[0.08] bg-brand-surface/40 p-8 md:p-10">
            <p className="eyebrow mb-2">Listino (prezzi IVA esclusa)</p>
            <p className="text-xs text-white/40 mb-6">Modifica gli importi: restano invariati nel riepilogo, l'IVA si aggiunge solo sul totale.</p>
            <div className="space-y-0">
              {cfg.listino.map((it) => (
                <div key={it.id} className="flex items-center justify-between gap-4 border-b border-white/[0.07] py-3.5">
                  <span className="text-sm text-white/75">{it.label}</span>
                  {it.tipo === "auto" ? (
                    <span className="text-xs text-white/40">calcolata sui km</span>
                  ) : (
                    <div className="flex items-center gap-2">
                      <input
                        data-testid={`admin-prezzo-${it.id}`}
                        type="number"
                        step="1"
                        min="0"
                        className="field !w-28 !py-2 text-right"
                        value={it.prezzo}
                        onChange={(e) => setPrezzo(it.id, e.target.value)}
                      />
                      <span className="text-xs text-white/40">EUR</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal className="mt-8">
          <button
            data-testid="admin-save"
            onClick={save}
            disabled={busy}
            className="inline-flex items-center justify-center gap-3 rounded-full bg-brand-orange px-10 py-5 text-xs font-semibold uppercase tracking-[0.2em] text-brand-ink hover:bg-[#ff9433] transition-colors duration-300 disabled:opacity-60 w-full md:w-auto"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={15} />}
            Salva configurazione
          </button>
          <p className="mt-4 text-xs text-white/40">
            Le modifiche sono immediatamente attive sul calcolatore di preventivi.
          </p>
        </Reveal>

        <Reveal className="mt-12">
          <div data-testid="admin-stats" className="rounded-3xl border border-white/[0.08] bg-brand-surface/40 p-8 md:p-10">
            <p className="eyebrow mb-6">Visite alle pagine</p>
            {!stats ? (
              <p className="text-sm text-white/50">Caricamento statistiche…</p>
            ) : stats.length === 0 ? (
              <p className="text-sm text-white/50">
                Nessuna visita registrata: il conteggio parte dalla pubblicazione del sito.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-white/40">
                      <th className="pb-3 font-medium">Pagina</th>
                      <th className="pb-3 font-medium text-right">Ultime 24h</th>
                      <th className="pb-3 font-medium text-right">Ultimi 7 giorni</th>
                      <th className="pb-3 font-medium text-right">Totale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.map((s) => (
                      <tr key={s.pagina} data-testid={`admin-stats-${s.pagina}`} className="border-t border-white/[0.06]">
                        <td className="py-3 text-white/80">{s.pagina === "/" ? "Home" : s.pagina}</td>
                        <td className="py-3 text-right text-white/60">{s.ultime_24h}</td>
                        <td className="py-3 text-right text-white/60">{s.ultimi_7g}</td>
                        <td className="py-3 text-right font-semibold text-brand-orange">{s.totale}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="mt-4 text-[11px] text-white/40">
              Visite reali di pagina, senza cookie, bot esclusi. Le visite a questo pannello non
              vengono registrate.
            </p>
          </div>
        </Reveal>

        <Reveal className="mt-12">
          <div data-testid="admin-iscritti-panel" className="rounded-3xl border border-white/[0.08] bg-brand-surface/40 p-8 md:p-10">
            <p className="eyebrow mb-6">Iscrizioni partner</p>
            {!iscritti ? (
              <p className="text-sm text-white/50">Caricamento iscrizioni…</p>
            ) : iscritti.length === 0 ? (
              <p className="text-sm text-white/50">
                Nessuna manifestazione iscritta: le iscrizioni arrivano dalla pagina Eventi
                Pubblici e dal modulo partner.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-white/40">
                      <th className="pb-3 font-medium">Data</th>
                      <th className="pb-3 font-medium">Organizzatore</th>
                      <th className="pb-3 font-medium">Manifestazione</th>
                      <th className="pb-3 font-medium">Spazio</th>
                      <th className="pb-3 font-medium">Contatti</th>
                      <th className="pb-3 font-medium">Stato trattativa</th>
                    </tr>
                  </thead>
                  <tbody>
                    {iscritti.map((x, i) => (
                      <tr key={x._id} data-testid={`admin-iscritto-${i}`} className="border-t border-white/[0.06] align-top">
                        <td className="py-4 text-white/50 whitespace-nowrap">{(x.created_at || "").slice(0, 10)}</td>
                        <td className="py-4 text-white/80">
                          {x.nome_cognome}
                          <span className="block text-xs text-white/40">{x.azienda}</span>
                        </td>
                        <td className="py-4 text-white/60">{x.tipologia_attivita}</td>
                        <td className="py-4 text-white/60">{x.spazio_disponibile}</td>
                        <td className="py-4 text-white/60">
                          <a href={`mailto:${x.email}`} className="block text-brand-orange hover:underline">{x.email}</a>
                          <span className="block text-xs">{x.telefono}</span>
                        </td>
                        <td className="py-4">
                          <select
                            data-testid={`admin-stato-select-${i}`}
                            value={x.stato || "Nuovo"}
                            onChange={(e) => cambiaStato(x._id, e.target.value)}
                            className="rounded-lg border border-white/15 bg-brand-ink/60 px-3 py-2 text-xs text-white focus:border-brand-orange focus:outline-none"
                          >
                            {["Nuovo", "Contattato", "In trattativa", "Accordato", "Archiviato"].map((s) => (
                              <option key={s} value={s}>
                                {s}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Reveal>

        <Reveal className="mt-12">
          <div data-testid="admin-reports-panel" className="rounded-3xl border border-white/[0.08] bg-brand-surface/40 p-8 md:p-10">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
              <p className="eyebrow">Report vendite eventi</p>
              <button
                type="button"
                data-testid="admin-report-new-btn"
                onClick={() => {
                  setREdit(null);
                  setRForm(EMPTY_REPORT);
                  setRShow((s) => !s);
                }}
                className="inline-flex items-center gap-2 rounded-full border border-brand-orange/50 px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-brand-orange hover:bg-brand-orange hover:text-brand-ink transition-colors duration-200"
              >
                <FilePlus2 size={13} /> Nuovo report
              </button>
            </div>
            <p className="text-xs text-white/40 mb-6">
              Riepilogo incassi da compilare dopo ogni manifestazione: drink registrati × quota a
              drink = quota spettante all'organizzatore, da rendicontare secondo l'accordo. Ogni
              report genera il PDF di rendicontazione da condividere con il partner.
            </p>

            {rShow && (
              <form onSubmit={salvaReport} data-testid="admin-report-form" className="mb-8 rounded-2xl border border-brand-orange/30 bg-brand-orange/[0.05] p-6 space-y-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-white/50 mb-2.5">Nome evento *</label>
                    <input
                      data-testid="admin-report-field-nome"
                      className="field"
                      placeholder="Es. Sagra dell'orso"
                      value={rForm.nome_evento}
                      onChange={(e) => setRForm((f) => ({ ...f, nome_evento: e.target.value }))}
                      required
                      minLength={2}
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-white/50 mb-2.5">Organizzazione *</label>
                    <input
                      data-testid="admin-report-field-organizzazione"
                      className="field"
                      placeholder="Es. Pro Loco, Comune, agenzia…"
                      value={rForm.organizzazione}
                      onChange={(e) => setRForm((f) => ({ ...f, organizzazione: e.target.value }))}
                      required
                      minLength={2}
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-white/50 mb-2.5">Data evento</label>
                    <input
                      type="date"
                      data-testid="admin-report-field-data"
                      className="field [color-scheme:dark]"
                      value={rForm.data_evento}
                      onChange={(e) => setRForm((f) => ({ ...f, data_evento: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-white/50 mb-2.5">Località</label>
                    <input
                      data-testid="admin-report-field-localita"
                      className="field"
                      placeholder="Es. Siena (SI)"
                      value={rForm.localita}
                      onChange={(e) => setRForm((f) => ({ ...f, localita: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-white/50 mb-2.5">Drink venduti *</label>
                    <input
                      type="number"
                      min="0"
                      data-testid="admin-report-field-drink"
                      className="field"
                      value={rForm.drink_venduti}
                      onChange={(e) => setRForm((f) => ({ ...f, drink_venduti: Math.max(0, Math.round(Number(e.target.value) || 0)) }))}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-white/50 mb-2.5">Quota a drink € *</label>
                    <input
                      type="number"
                      step="0.1"
                      min="0"
                      data-testid="admin-report-field-quota"
                      className="field"
                      value={rForm.quota_drink}
                      onChange={(e) => setRForm((f) => ({ ...f, quota_drink: Math.max(0, Number(e.target.value) || 0) }))}
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-white/50 mb-2.5">Note</label>
                  <textarea
                    rows={2}
                    data-testid="admin-report-field-note"
                    className="field resize-none"
                    value={rForm.note}
                    onChange={(e) => setRForm((f) => ({ ...f, note: e.target.value }))}
                  />
                </div>
                {rEdit && (
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-white/50 mb-2.5">Stato rendicontazione</label>
                    <select
                      data-testid="admin-report-field-stato"
                      className="field appearance-none"
                      value={rForm.stato}
                      onChange={(e) => setRForm((f) => ({ ...f, stato: e.target.value }))}
                    >
                      {["Bozza", "Inviata", "Pagata"].map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-5">
                  <p className="text-sm text-white/70">
                    Quota spettante:{" "}
                    <span data-testid="admin-report-quota-totale" className="font-display text-2xl font-semibold text-brand-orange">
                      {eur(Math.round(rForm.drink_venduti * rForm.quota_drink * 100) / 100)}
                    </span>
                  </p>
                  <button
                    type="submit"
                    data-testid="admin-report-save-btn"
                    disabled={rBusy}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-orange px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-brand-ink hover:bg-[#ff9433] transition-colors duration-300 disabled:opacity-60"
                  >
                    {rBusy ? <Loader2 size={15} className="animate-spin" /> : <Save size={14} />}
                    {rEdit ? "Aggiorna report" : "Salva report"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setRShow(false);
                      setREdit(null);
                    }}
                    className="rounded-full border border-white/15 px-6 py-3 text-[11px] uppercase tracking-[0.15em] text-white/60 hover:border-white/40 transition-colors duration-200"
                  >
                    Annulla
                  </button>
                </div>
              </form>
            )}

            {!reports ? (
              <p className="text-sm text-white/50">Caricamento report…</p>
            ) : reports.length === 0 && !rShow ? (
              <p className="text-sm text-white/50">
                Nessun report: premi "Nuovo report" dopo ogni manifestazione per registrare i drink
                venduti e la quota del partner socio.
              </p>
            ) : reports.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-white/40">
                      <th className="pb-3 font-medium">Evento</th>
                      <th className="pb-3 font-medium">Organizzazione</th>
                      <th className="pb-3 font-medium text-right">Drink</th>
                      <th className="pb-3 font-medium text-right">Quota</th>
                      <th className="pb-3 font-medium">Stato</th>
                      <th className="pb-3 font-medium">Rendiconto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map((r, i) => (
                      <tr key={r._id} data-testid={`admin-report-row-${i}`} className="border-t border-white/[0.06] align-top">
                        <td className="py-4 text-white/80">
                          {r.nome_evento}
                          <span className="block text-xs text-white/40">
                            {r.data_evento || "-"}
                            {r.localita ? ` · ${r.localita}` : ""}
                          </span>
                        </td>
                        <td className="py-4 text-white/60">{r.organizzazione}</td>
                        <td className="py-4 text-right text-white/60">{r.drink_venduti}</td>
                        <td className="py-4 text-right font-semibold text-brand-orange">{eur(r.quota_totale)}</td>
                        <td className="py-4">
                          <select
                            data-testid={`admin-report-stato-select-${i}`}
                            value={r.stato || "Bozza"}
                            onChange={(e) => cambiaStatoReport(r._id, e.target.value)}
                            className="rounded-lg border border-white/15 bg-brand-ink/60 px-3 py-2 text-xs text-white focus:border-brand-orange focus:outline-none"
                          >
                            {["Bozza", "Inviata", "Pagata"].map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        </td>
                        <td className="py-4">
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              data-testid={`admin-report-pdf-${i}`}
                              onClick={() => scaricaRendiconto(r)}
                              className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-4 py-2 text-[11px] uppercase tracking-[0.12em] text-white/70 hover:border-brand-orange hover:text-brand-orange transition-colors duration-200"
                            >
                              <FileDown size={12} /> PDF
                            </button>
                            <button
                              type="button"
                              data-testid={`admin-report-edit-${i}`}
                              onClick={() => apriModificaReport(r)}
                              className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-4 py-2 text-[11px] uppercase tracking-[0.12em] text-white/70 hover:border-brand-orange hover:text-brand-orange transition-colors duration-200"
                            >
                              <Pencil size={12} /> Modifica
                            </button>
                            <button
                              type="button"
                              data-testid={`admin-report-delete-${i}`}
                              onClick={() => {
                                if (window.confirm(`Eliminare il report "${r.nome_evento}"?`)) eliminaReport(r._id);
                              }}
                              className="inline-flex items-center gap-1.5 rounded-full border border-white/20 px-4 py-2 text-[11px] uppercase tracking-[0.12em] text-white/50 hover:border-red-500 hover:text-red-400 transition-colors duration-200"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </Reveal>
      </div>
    </div>
  );
}
